import { useLanguageAwareRouting } from '@gofranz/common-components';
import type { CharacterInfo } from '@gofranz/gpt-muse-common';
import {
  Avatar,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Group,
  Image,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useRustyState } from '../../state';

// Helper function to get character image path
const getCharacterImagePath = (characterId: string): string => {
  return `/characters/${characterId}.png`;
};

export function Welcome() {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { createLanguageURL } = useLanguageAwareRouting();
  const [characters, setCharacters] = useState<CharacterInfo[]>([]);

  // Load available characters
  const loadCharacters = useCallback(async () => {
    try {
      const api = useRustyState.getState().api;
      const charactersData = await api.listCharacters();
      setCharacters(charactersData);
    } catch (error) {
      console.error('Failed to load characters:', error);
    }
  }, []);

  useEffect(() => {
    loadCharacters();
  }, [loadCharacters]);

  return (
    <>
      <Container>
        <Image
          src="/logo.svg"
          fit="contain"
          alt={t('brand.logoAlt')}
          mt={100}
          width={200}
          height={200}
        />
        <Title size={50} ta="center">
          <>
            <Text
              inherit
              variant="gradient"
              component="span"
              gradient={{ from: theme.colors['deep-teal'][6], to: theme.colors['deep-teal'][6] }}
            >
              {t('brand.name')}
            </Text>
            <Text
              inherit
              variant="gradient"
              component="span"
              gradient={{ from: theme.colors['muted-aqua'][6], to: theme.colors['muted-aqua'][6] }}
            >
              {t('brand.nameSecond')}
            </Text>
          </>
        </Title>
        <Text ta="center" size="xl" maw={580} mx="auto" mt="xl" lh="xs">
          <b>{t('welcome.tagline')}</b>
          <br />
          {t('welcome.description')}
        </Text>
        <Text c="dimmed" ta="center" size="lg" maw={680} mx="auto" mt="md">
          {t('welcome.subDescription')}
        </Text>
      </Container>

      <Divider mt="md" mb="md" />

      {/* Social Proof Stats Section */}
      <Container mt="xl" mb="xl">
        <Title order={3} ta="center" mb="lg" fw={600}>
          {t('welcome.stats.title')}
        </Title>
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xl">
          <Box ta="center">
            <Text size="2.5rem" fw={700} c={theme.colors['deep-teal'][6]}>
              {t('welcome.stats.activeUsers')}
            </Text>
            <Text size="sm" c="dimmed" fw={500}>
              {t('welcome.stats.activeUsersLabel')}
            </Text>
          </Box>
          <Box ta="center">
            <Text size="2.5rem" fw={700} c={theme.colors['deep-teal'][6]}>
              {t('welcome.stats.modelsAvailable')}
            </Text>
            <Text size="sm" c="dimmed" fw={500}>
              {t('welcome.stats.modelsAvailableLabel')}
            </Text>
          </Box>
          <Box ta="center">
            <Text size="2.5rem" fw={700} c={theme.colors['deep-teal'][6]}>
              {t('welcome.stats.uptime')}
            </Text>
            <Text size="sm" c="dimmed" fw={500}>
              {t('welcome.stats.uptimeLabel')}
            </Text>
          </Box>
          <Box ta="center">
            <Text size="2.5rem" fw={700} c={theme.colors['deep-teal'][6]}>
              {t('welcome.stats.responseTime')}
            </Text>
            <Text size="sm" c="dimmed" fw={500}>
              {t('welcome.stats.responseTimeLabel')}
            </Text>
          </Box>
        </SimpleGrid>
      </Container>

      <Divider mt="xl" mb="xl" />

      {/* Enhanced Features Section */}
      <Container mt="xl" mb="xl">
        <Title order={3} ta="center" mb="lg" fw={600}>
          {t('welcome.features.title')}
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="xl">
          <Box>
            <Title order={4}>{t('welcome.features.privacy.title')}</Title>
            <Text>{t('welcome.features.privacy.description')}</Text>
          </Box>

          <Box>
            <Title order={4}>{t('welcome.features.models.title')}</Title>
            <Text>{t('welcome.features.models.description')}</Text>
          </Box>

          <Box>
            <Title order={4}>{t('welcome.features.speed.title')}</Title>
            <Text>{t('welcome.features.speed.description')}</Text>
          </Box>

          <Box>
            <Title order={4}>{t('welcome.features.pricing.title')}</Title>
            <Text>{t('welcome.features.pricing.description')}</Text>
          </Box>

          <Box>
            <Title order={4}>{t('welcome.features.characters.title')}</Title>
            <Text>{t('welcome.features.characters.description')}</Text>
          </Box>

          <Box>
            <Title order={4}>{t('welcome.features.images.title')}</Title>
            <Text>{t('welcome.features.images.description')}</Text>
          </Box>
        </SimpleGrid>
      </Container>

      <Divider mt="xl" mb="xl" />

      {/* Characters Section */}
      <Container mt="xl" mb="xl">
        <Title order={3} ta="center" mb="lg" fw={600}>
          {t('welcome.characters.title')}
        </Title>
        <Text ta="center" size="md" c="dimmed" mb="xl" maw={600} mx="auto">
          {t('welcome.characters.description')}
        </Text>
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="lg">
          {characters.map((character) => (
            <Card
              key={character.id}
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
              style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = '';
              }}
              component={Link}
              to={createLanguageURL(`/chat?character=${character.id}`)}
            >
              <Stack align="center" gap="sm">
                <Image
                  src={getCharacterImagePath(character.id)}
                  alt={character.name}
                  w={80}
                  h={80}
                  radius="lg"
                  fallbackSrc="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiByeD0iMTIiIGZpbGw9IiM4ODg4ODgiLz4KPHA + CzQwIDI4QzQzLjMxMzcgMjggNDYgMzAuNjg2MyA0NiAzNEM0NiAzNy4zMTM3IDQzLjMxMzcgNDAgNDAgNDBDMzYuNjg2MyA0MCAzNCAzNy4zMTM3IDM0IDM0QzM0IDMwLjY4NjMgMzYuNjg2MyAyOCA0MCAyOFoiIGZpbGw9IndoaXRlIi8+CjxwYXRoIGQ9Ik00MCA2MEM0NiA2MCA1MiA1NiA1MiA1MEM1MiA0NiA0NCA0MiA0MCA0MkMzNiA0MiAyOCA0NiAyOCA1MEMyOCA1NiAzNCA2MCA0MCA2MFoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPg=="
                />
                <Box ta="center">
                  <Text fw={600} size="sm" lineClamp={1}>
                    {character.name}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={1} mt={2}>
                    {character.description}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={2} mt={2}>
                    {character.description_long}
                  </Text>
                </Box>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      </Container>

      <Divider mt="xl" mb="xl" />

      {/* Use Cases Section */}
      <Container mt="xl" mb="xl">
        <Title order={3} ta="center" mb="lg" fw={600}>
          {t('welcome.useCases.title')}
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="xl">
          <Card shadow="xs" padding="lg" radius="md" withBorder>
            <Title order={5} mb="sm">{t('welcome.useCases.creative.title')}</Title>
            <Text size="sm">{t('welcome.useCases.creative.description')}</Text>
          </Card>
          <Card shadow="xs" padding="lg" radius="md" withBorder>
            <Title order={5} mb="sm">{t('welcome.useCases.design.title')}</Title>
            <Text size="sm">{t('welcome.useCases.design.description')}</Text>
          </Card>
          <Card shadow="xs" padding="lg" radius="md" withBorder>
            <Title order={5} mb="sm">{t('welcome.useCases.development.title')}</Title>
            <Text size="sm">{t('welcome.useCases.development.description')}</Text>
          </Card>
          <Card shadow="xs" padding="lg" radius="md" withBorder>
            <Title order={5} mb="sm">{t('welcome.useCases.research.title')}</Title>
            <Text size="sm">{t('welcome.useCases.research.description')}</Text>
          </Card>
          <Card shadow="xs" padding="lg" radius="md" withBorder>
            <Title order={5} mb="sm">{t('welcome.useCases.business.title')}</Title>
            <Text size="sm">{t('welcome.useCases.business.description')}</Text>
          </Card>
          <Card shadow="xs" padding="lg" radius="md" withBorder>
            <Title order={5} mb="sm">{t('welcome.useCases.learning.title')}</Title>
            <Text size="sm">{t('welcome.useCases.learning.description')}</Text>
          </Card>
        </SimpleGrid>
      </Container>

      <Divider mt="xl" mb="xl" />

      {/* Comparison Section */}
      <Container mt="xl" mb="xl">
        <Title order={3} ta="center" mb="lg" fw={600}>
          {t('welcome.comparison.title')}
        </Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="sm">
              <Title order={5} c={theme.colors['deep-teal'][6]}>
                {t('welcome.comparison.gptmuse')}
              </Title>
              <Text size="sm">✅ {t('welcome.comparison.features.privacy')}</Text>
              <Text size="sm">✅ {t('welcome.comparison.features.multipleModels')}</Text>
              <Text size="sm">✅ {t('welcome.comparison.features.payPerUse')}</Text>
              <Text size="sm">✅ {t('welcome.comparison.features.noLockIn')}</Text>
              <Text size="sm">✅ {t('welcome.comparison.features.characters')}</Text>
              <Text size="sm">✅ {t('welcome.comparison.features.images')}</Text>
            </Stack>
          </Card>
          <Card shadow="sm" padding="lg" radius="md" withBorder bg={theme.colors.gray[0]}>
            <Stack gap="sm">
              <Title order={5} c={theme.colors.gray[6]}>
                {t('welcome.comparison.others')}
              </Title>
              <Text size="sm" c="dimmed">❌ {t('welcome.comparison.limitations.dataTraining')}</Text>
              <Text size="sm" c="dimmed">❌ {t('welcome.comparison.limitations.singleModel')}</Text>
              <Text size="sm" c="dimmed">❌ {t('welcome.comparison.limitations.subscriptions')}</Text>
              <Text size="sm" c="dimmed">❌ {t('welcome.comparison.limitations.vendorLockIn')}</Text>
              <Text size="sm" c="dimmed">❌ {t('welcome.comparison.limitations.limitedFeatures')}</Text>
              <Text size="sm" c="dimmed">❌ {t('welcome.comparison.limitations.basicChat')}</Text>
            </Stack>
          </Card>
        </SimpleGrid>
      </Container>

      <Container ta="center" mt="4rem" mb="4rem" maw={600} mx="auto">
        {/* Chat Interface Preview */}
        <Box mb="xl">
          <Title order={3} ta="center" mb="md" c={theme.colors['deep-teal'][7]}>
            {t('welcome.demo.title')}
          </Title>
          <Paper
            p="lg"
            radius="lg"
            withBorder
            style={{
              maxWidth: 520,
              margin: '0 auto',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              background: `linear-gradient(135deg, ${theme.colors.gray[0]} 0%, ${theme.colors.blue[0]} 100%)`,
              border: `2px solid ${theme.colors['deep-teal'][2]}`,
            }}
            component={Link}
            to={createLanguageURL('/chat')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = `0 8px 32px ${theme.colors['deep-teal'][2]}`;
              e.currentTarget.style.borderColor = theme.colors['deep-teal'][4];
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = '';
              e.currentTarget.style.boxShadow = '';
              e.currentTarget.style.borderColor = theme.colors['deep-teal'][2];
            }}
          >
            <Stack gap="md">
              {/* Header */}
              <Group justify="space-between" mb="sm">
                <Group gap="xs">
                  <Box
                    w={8}
                    h={8}
                    bg="red"
                    style={{ borderRadius: '50%' }}
                  />
                  <Box
                    w={8}
                    h={8}
                    bg="orange"
                    style={{ borderRadius: '50%' }}
                  />
                  <Box
                    w={8}
                    h={8}
                    bg="green"
                    style={{ borderRadius: '50%' }}
                  />
                </Group>
                <Text size="xs" c="dimmed" fw={500}>
                  {t('welcome.demo.chatTitle')}
                </Text>
              </Group>

              {/* User Message */}
              <Group justify="flex-end" align="flex-start">
                <Paper
                  p="md"
                  radius="xl"
                  bg={theme.colors.blue[6]}
                  style={{ maxWidth: '75%' }}
                >
                  <Text c="white" size="sm" fw={500}>
                    {t('welcome.demo.userMessage')}
                  </Text>
                </Paper>
                <Avatar size="sm" bg={theme.colors.blue[6]} c="white">
                  😊
                </Avatar>
              </Group>

              {/* AI Response */}
              <Group justify="flex-start" align="flex-start">
                <Avatar size="sm" bg={theme.colors['deep-teal'][6]} c="white">
                  🤖
                </Avatar>
                <Paper
                  p="md"
                  radius="xl"
                  bg="white"
                  style={{ 
                    maxWidth: '75%', 
                    border: `1px solid ${theme.colors.gray[3]}`,
                    boxShadow: theme.shadows.sm,
                  }}
                >
                  <Text size="sm" c={theme.colors.gray[8]} lh={1.4}>
                    {t('welcome.demo.aiResponse')}
                  </Text>
                </Paper>
              </Group>

              {/* Action Call */}
              <Paper
                p="md"
                radius="md"
                bg={theme.colors['deep-teal'][1]}
                style={{ border: `1px dashed ${theme.colors['deep-teal'][4]}` }}
              >
                <Group justify="center" gap="xs">
                  <Text size="sm" fw={600} c="white">
                    {t('welcome.demo.callToAction')}
                  </Text>
                </Group>
              </Paper>
            </Stack>
          </Paper>
        </Box>

        <Divider my="xl" />

        <Card shadow="xs" padding="lg" radius="md" withBorder maw={360} w="100%" mx="auto">
          <Stack align="center">
            <Title order={4} ta="center" mb={0} fw={500}>
              {t('welcome.cta.loginTitle')}
            </Title>
            <Text c="dimmed" ta="center" size="sm" maw={280} mx="auto">
              {t('welcome.cta.loginDescription')}
            </Text>
            <Button
              component={Link}
              to={createLanguageURL('/login')}
              variant="outline"
              size="md"
              radius="md"
              px={30}
            >
              {t('welcome.cta.loginButton')}
            </Button>
          </Stack>
        </Card>
      </Container>
    </>
  );
}
